import { useEffect, useState, type FormEvent } from "react";
import { businessInfo } from "@/mocks/siteData";
import useScrollLock from "@/hooks/useScrollLock";

type ConsultModalProps = {
  open: boolean;
  onClose: () => void;
  prefillMessage?: string;
};

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/daojul8kt7a399dmsce0";

type Status = "idle" | "loading" | "success" | "error";

export default function ConsultModal({
  open,
  onClose,
  prefillMessage,
}: ConsultModalProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setErrorMsg("");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          isSpam
            ? "제출이 정상적으로 처리되지 않았습니다. 입력 내용을 다시 확인해 주세요."
            : String(serverMsg) || "전송에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-background-50/85 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="modal-scroll relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors duration-300 hover:text-primary-400 cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {status === "success" ? (
          <div className="py-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary-700/60 bg-primary-950/50 text-primary-400">
              <i className="ri-check-line text-2xl" />
            </div>
            <h3 className="font-subheading mt-5 text-xl font-semibold text-foreground-950">
              상담 예약이 접수되었습니다
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              담당 변호사가 확인 후 빠르게 연락드리겠습니다.
              <br />
              급하신 경우 아래로 바로 연결해 주세요.
            </p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <a
                href={`tel:${businessInfo.phone.replace(/-/g, "")}`}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer"
              >
                전화 연결
              </a>
              <a
                href={businessInfo.kakao}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300/60 px-6 py-3 text-sm text-foreground-700 transition-colors duration-300 hover:border-primary-500 hover:text-primary-400 cursor-pointer"
              >
                카카오톡
              </a>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 text-sm text-foreground-500 underline-offset-4 transition-colors duration-300 hover:text-foreground-800 hover:underline cursor-pointer"
            >
              닫기
            </button>
          </div>
        ) : (
          <>
            <div className="font-label text-[10px] text-primary-400">Consultation</div>
            <h3 className="font-subheading mt-2 text-xl font-semibold text-foreground-950 md:text-2xl">
              무료 상담예약
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              간단한 정보를 남겨주시면 전문 변호사가 신속하게 연락드립니다.
              <br />
              상담 내용은 철저히 비밀 보호됩니다.
            </p>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="consult-name" className="mb-1.5 block text-xs text-foreground-600">
                    성함 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="consult-name"
                    name="name"
                    type="text"
                    required
                    placeholder="성함을 입력해 주세요"
                    className="w-full rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="consult-phone" className="mb-1.5 block text-xs text-foreground-600">
                    연락처 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="consult-phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    required
                    placeholder="010-0000-0000"
                    className="w-full rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="consult-email" className="mb-1.5 block text-xs text-foreground-600">
                  이메일
                </label>
                <input
                  id="consult-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="consult-type" className="mb-1.5 block text-xs text-foreground-600">
                  상담분야
                </label>
                <select
                  id="consult-type"
                  name="consult_type"
                  defaultValue="개인회생"
                  className="w-full rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-500 focus:outline-none cursor-pointer"
                >
                  <option>개인회생</option>
                  <option>개인파산</option>
                  <option>채무조정 / 기타</option>
                </select>
              </div>

              <div>
                <label htmlFor="consult-message" className="mb-1.5 block text-xs text-foreground-600">
                  문의내용
                </label>
                <textarea
                  id="consult-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  defaultValue={prefillMessage}
                  placeholder="채무 규모, 현재 상황 등을 간단히 적어주세요 (선택, 500자 이내)"
                  className="w-full resize-none rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="consent-aux-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <label className="flex items-start gap-2.5 text-xs text-foreground-600">
                <input
                  type="checkbox"
                  name="agree"
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary-500"
                />
                <span>개인정보 수집·이용에 동의합니다. (상담 목적, 보유기간 1년)</span>
              </label>

              {status === "error" ? (
                <p className="rounded-md border border-accent-700/50 bg-accent-950/40 px-4 py-2.5 text-xs text-accent-300">
                  {errorMsg}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                {status === "loading" ? "전송 중..." : "상담 예약하기"}
              </button>

              <div className="flex items-center gap-3 pt-1">
                <span className="h-px flex-1 bg-background-200/60" />
                <span className="text-xs text-foreground-500">또는 즉시 연결</span>
                <span className="h-px flex-1 bg-background-200/60" />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href={`tel:${businessInfo.phone.replace(/-/g, "")}`}
                  className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-700 transition-colors duration-300 hover:border-primary-500 hover:text-primary-400 cursor-pointer"
                >
                  전화상담
                </a>
                <a
                  href={businessInfo.kakao}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-700 transition-colors duration-300 hover:border-primary-500 hover:text-primary-400 cursor-pointer"
                >
                  카카오톡
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}